import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { formatPhone } from "shared/utils";
import {
  crmAnalyticsApi,
  type DateRangeParams,
  type LeasingFunnelStep
} from "api/crm-analytics";
import { getErrorMessage } from "utils/error";
import { AnalyticsDetailModal } from "./AnalyticsDetailModal";
import { DrilldownRow } from "./DrilldownRow";
import type { UnitFilterParams } from "./UnitsFilter";
import {
  DEFAULT_PER_PAGE,
  DRILLDOWN_ONE_DATE_GRID_CLASSNAME
} from "../constants";

const COLUMNS = [
  { label: "Name" },
  { label: "Property" },
  { label: "Unit" },
  { label: "Date created" }
];

export const LeasingFunnelModal = ({
  opened,
  onClose,
  step,
  dateParams,
  filterParams
}: {
  opened: boolean;
  onClose: () => void;
  step: LeasingFunnelStep | null;
  dateParams: DateRangeParams;
  filterParams?: UnitFilterParams;
}) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [opened, step?.key, dateParams, filterParams]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "CRM_ANALYTICS_LEASING_FUNNEL_DRILLDOWN",
      step?.key,
      dateParams,
      filterParams,
      page
    ],
    queryFn: () =>
      crmAnalyticsApi.getLeasingFunnelDrilldown({
        step_key: step!.key,
        ...dateParams,
        ...filterParams,
        page,
        per_page: DEFAULT_PER_PAGE
      }),
    enabled: opened && step !== null,
    placeholderData: keepPreviousData,
    meta: {
      onError: (err: unknown) => toast.error(getErrorMessage(err))
    }
  });

  if (!step) return null;

  const leads = data?.items ?? [];

  const handleLeadClick = (leadId: number) => {
    onClose();
    navigate(`/leads/${leadId}`);
  };

  return (
    <AnalyticsDetailModal
      opened={opened}
      onClose={onClose}
      title={step.label}
      subtitle={`${(data?.total_count ?? 0).toLocaleString("en-US")} leads`}
      gridClassName={DRILLDOWN_ONE_DATE_GRID_CLASSNAME}
      columns={COLUMNS}
      isLoading={isLoading}
      isFetching={isFetching}
      isEmpty={leads.length === 0}
      emptyText="No leads reached this step"
      pagination={{
        total: data?.page_count ?? 0,
        current: page,
        onChange: setPage
      }}
    >
      {leads.map((lead) => (
        <DrilldownRow
          key={lead.id}
          name={`${lead.first_name} ${lead.last_name}`}
          contactDetail={
            lead.primary_phone
              ? formatPhone(lead.primary_phone)
              : lead.primary_email
          }
          propertyName={lead.property?.name ?? null}
          propertyAddress={lead.property?.address.address1 ?? null}
          unitTitle={lead.unit?.title ?? null}
          gridClassName={DRILLDOWN_ONE_DATE_GRID_CLASSNAME}
          dates={[{ value: lead.created_at }]}
          onClick={() => handleLeadClick(lead.id)}
        />
      ))}
    </AnalyticsDetailModal>
  );
};
